import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { AuthModal } from '../components/auth/AuthModal';
import { FeatureFlagRealtimeDemo } from '../components/examples/FeatureFlagRealtimeDemo';
import { GamificationGate } from '../components/GamificationGate';
import ShareModal from '../components/ShareModal';
import { Report } from '../hooks/useReports';



const HomePage = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useFirebase();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareReport, setShareReport] = useState<Report | null>(null);


  const handleGetStarted = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      navigate('/treasures');
    }
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setShareReport(null);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Gradient Background - Mobile Optimized */}
      <div className="relative bg-gradient-to-br from-teddy via-sky-neutral to-teddy-200 safe-area-top">
        <div className="max-w-7xl mx-auto mobile-padding py-8 sm:py-12 md:py-16">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-display font-bold text-urban mb-4 sm:mb-6 leading-tight">
              Turn Trash into
              <span className="text-treasure block sm:inline"> Treasure</span>
            </h1>
            <p className="text-lg sm:text-xl text-urban-400 mb-8 sm:mb-12 max-w-3xl mx-auto px-2">
              Discover amazing curbside finds in your community. Share your discoveries, 
              help others find treasures, and promote sustainability through reuse.
            </p>

            {/* User Status - Mobile Optimized */}
            {user && userProfile && (
              <div className="mb-6 sm:mb-8">
                <p className="text-base sm:text-lg text-urban mb-3 sm:mb-4 px-2">
                  Welcome back, <span className="font-semibold">{userProfile.displayName || user.displayName}</span>!
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 text-sm px-2">
                  <GamificationGate feature="coins">
                    <span className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full touch-target">
                      <span className="text-treasure">🪙</span>
                      <span className="font-medium text-urban">{userProfile.goldCoins} coins</span>
                    </span>
                  </GamificationGate>
                  <GamificationGate feature="coins">
                    <span className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full touch-target">
                      <span className="text-cardboard">🎫</span>
                      <span className="font-medium text-urban">{userProfile.farAwayTokens} tokens</span>
                    </span>
                  </GamificationGate>
                  <span className="capitalize bg-white/80 px-4 py-2 rounded-full font-medium text-urban touch-target">
                    {userProfile.subscriptionTier}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      

     

      {/* Guidelines Section - Mobile Optimized */}
      <div className="bg-sky-neutral py-8 sm:py-12">
  <div className="max-w-7xl mx-auto mobile-padding">
    <div className="text-center mb-10 sm:mb-16">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-urban mb-3 sm:mb-4">
        📸 Photo Guidelines
      </h2>
      <p className="text-base sm:text-lg text-urban-400 px-2">
        To keep the Curbside Quest community helpful, respectful, and high-quality, please follow these simple guidelines when taking and uploading photos.
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
      {/* ✅ DOs */}
      <div className="bg-white rounded-2xl shadow p-6 text-left">
        <h3 className="text-lg sm:text-xl font-semibold mb-4 text-green-600">✅ DOs</h3>
        <ul className="list-disc list-inside space-y-2 text-urban-500">
          <li>Take clear photos in good lighting – blurry or dark photos make treasures harder to spot.</li>
          <li>Capture the full pickup area so people can understand what’s available.</li>
          <li>Take multiple photos if needed:
            <ul className="list-disc list-inside ml-6">
              <li>One wide shot of the whole setup</li>
              <li>Closer shots for specific items (e.g. cot, lamp, bike)</li>
            </ul>
          </li>
          <li>Ensure the location is easy to identify without compromising privacy.</li>
          <li>Update with new photos if the pickup spot changes or items are taken/added.</li>
          <li>Highlight condition or defects if visible (e.g. broken leg on a table).</li>
        </ul>
      </div>

      {/* 🚫 DON'Ts */}
      <div className="bg-white rounded-2xl shadow p-6 text-left">
        <h3 className="text-lg sm:text-xl font-semibold mb-4 text-red-600">🚫 DON’Ts</h3>
        <ul className="list-disc list-inside space-y-2 text-urban-500">
          <li>Don’t include identifiable faces or people in the photo.</li>
          <li>Avoid showing houses, number plates, or other identifying features in the background.</li>
          <li>Do not include offensive, graphic, or unsafe items – those will be removed.</li>
          <li>Avoid using filters or heavily edited images – keep it natural and representative.</li>
          <li>Don’t upload duplicates of the same photo from the same angle.</li>
        </ul>
      </div>
    </div>

    {/* ⚠️ Moderation Note */}
    <div className="mt-10 bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-left">
      <h3 className="text-lg sm:text-xl font-semibold mb-4 text-yellow-700">⚠️ Note on Moderation</h3>
      <p className="text-urban-500 mb-2">
        We reserve the right to hide or remove photos that include:
      </p>
      <ul className="list-disc list-inside space-y-2 text-urban-500">
        <li>People or private property inappropriately</li>
        <li>Potentially sensitive, inappropriate, or unsafe content</li>
        <li>Any content that violates community guidelines</li>
      </ul>
      <p className="text-urban-500 mt-4">
        Help us keep the hunt fun, respectful, and focused on sustainable sharing! 🧡
      </p>
    </div>
  </div>
</div>

      {/* Feature Flag Demo - Development Only */}
      {import.meta.env.DEV && (
        <div className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto mobile-padding">
            <FeatureFlagRealtimeDemo />
          </div>
        </div>
      )}

      {/* CTA Section - Mobile Optimized */}
      <div className="bg-urban py-8 sm:py-12 safe-area-bottom">
        <div className="max-w-7xl mx-auto mobile-padding text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3 sm:mb-4 leading-tight">
            Ready to Start Your Treasure Hunt?
          </h2>
          <p className="text-lg sm:text-xl text-urban-100 mb-6 sm:mb-8 px-2">
            Join thousands of community members making a difference
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-treasure hover:bg-treasure-600 text-urban font-semibold px-8 py-4 rounded-xl transition-colors touch-target text-base sm:text-lg"
            aria-label={user ? 'Continue treasure hunting' : 'Get started with Curbside Quest'}
          >
            {user ? 'Continue Hunting' : 'Get Started Today'}
          </button>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signup"
      />

      {/* Share Modal */}
      {shareReport && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
          reportId={shareReport.id}
          reportTitle={shareReport.tags?.join(', ') || 'Curbside Treasure'}
          reportImage={shareReport.images?.[0]?.url}
          reportLocation={`${shareReport.location.suburb}, ${shareReport.location.postcode}`}
        />
      )}
    </div>
  );
};

export default HomePage;